import { defineStore } from 'pinia';
import { DefaultApolloClient, provideApolloClient, useApolloClient } from '@vue/apollo-composable';
import GraphQLClientFactory from '@/lib/GraphQLClientFactory';
import config from '@/temp/config';
import tierDescriptionQueryGraphQL from './roadside-assistance-product.query.graphql';
import { RoadsideAssistanceService as _roadsideAssistanceService } from '@/services/store/roadside-assistance.service';
import { RoadsideAssistance, TriageAnswer } from '@/interfaces/entities/roadside-assistance.interface';
import { inject, ref } from 'vue';
import { IRoadsideContentInterface } from '@/interfaces/self-service/roadside.interface';
import { ApolloClient } from '@apollo/client';

export const useRoadsideAssistance = defineStore('RoadsideAssistance', () => {
  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //

  const breakdownCauses = ref<TriageAnswer[]>([]);

  // ----- apollo client (inject first, fallback to factory)
  const injected = inject<ApolloClient<any> | null>(DefaultApolloClient, null);
  const client: ApolloClient<any> = injected ?? GraphQLClientFactory(config.graphQLEndpoint, true);

  async function getServiceability(latitude: number, longitude: number): Promise<RoadsideAssistance> {
    const response = await _roadsideAssistanceService.getServiceability(latitude, longitude);
    // read response's triagequestion filter by id TriageQuestion and set breakdownCauses
    const triageQuestion = filterQuestionsById(response.triageQuestions, 'BREAKDOWN_REASON');
    if (triageQuestion !== null) {
      breakdownCauses.value = triageQuestion.answers;
    }
    return response;
  }

  function filterQuestionsById(questions: any[], id: string) {
    return questions.find((question) => question.id === id);
  }

  async function getRsaProductContent(productCode: string | undefined): Promise<IRoadsideContentInterface> {
    if (!productCode) return {};
    const roadsideAssistanceProductsGraphQLPath =
      '/sitecore/content/RACQFormsGroup/RACQForms20/Data/Roadside Assistance Products/';

    const { data } = await client.query({
      query: tierDescriptionQueryGraphQL,
      variables: { path: `${roadsideAssistanceProductsGraphQLPath}${productCode}` },
    });

    const item = data?.item ?? {};
    console.log(item);
    return {
      tierDescription: item.tierDescription?.rendered ?? item.tierDescription?.value ?? '',
      productName: item.productName?.rendered ?? item.productName?.value ?? '',
      apttusProductCode: item.apttusProductCode?.rendered ?? item.apttusProductCode?.value ?? '',
      subTitle: item.subTitle?.rendered ?? item.subTitle?.value ?? '',
      description: item.description?.rendered ?? item.description?.value ?? '',
      memberCover: item.memberCover?.rendered ?? item.memberCover?.value ?? '',
      deviceCover: item.deviceCover?.rendered ?? item.deviceCover?.value ?? '',
      attendance: item.attendance?.rendered ?? item.attendance?.value ?? '',
      infoText: item.infoText?.rendered ?? item.infoText?.value ?? '',
      towingAnyDirection: item.towingAnyDirection?.rendered ?? item.towingAnyDirection?.value ?? '',
      vehicleCover: item.vehicleCover?.rendered ?? item.vehicleCover?.value ?? '',
      rsaAssistance: item.rsaAssistance?.rendered ?? item.rsaAssistance?.value ?? '',
      isBusiness: item.isBusiness?.value == '1',
      policyBanners:
        item.policyBanners?.targetItems?.map((banner: any) => ({
          collapsed: banner.collapsed?.rendered == '1' || banner.collapsed?.value == '1',
          content: banner.content?.rendered ?? banner.content?.value ?? '',
          ctaLabel: banner.ctaLabel?.rendered ?? banner.ctaLabel?.value ?? '',
          ctaLink: banner.ctaLink?.rendered ?? banner.ctaLink?.value ?? '',
          testId: banner.testId?.rendered ?? banner.testId?.value ?? '',
        })) ?? [],
    };
  }

  return {
    getServiceability,
    breakdownCauses,
    getRsaProductContent,
  };
});
